// Per-course post-completion survey state, in localStorage.
//
// Two flags per course:
//   - submitted_at: ISO string when the learner sent a response
//   - skipped_at:   ISO string when the learner dismissed the prompt
// Either flag means "do not show the survey card on /certificate again."
//
// Plus a stable per-browser session_id (random UUID) used as the soft dedupe
// key on the backend until auth lands. Once login ships the server should
// link this id to the resolved user_id so historical responses don't get
// orphaned.

const STATE_KEY_PREFIX = 'lo:survey_state:';
const SESSION_KEY = 'lo:survey_session_id';

function safeRead(key) {
  try {
    if (typeof window === 'undefined') return null;
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeWrite(key, value) {
  try {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(key, value);
  } catch {
    // Storage may be disabled (Safari private mode etc.). The survey is
    // optional — if we can't persist state the worst case is the learner
    // sees the prompt again on the next visit.
  }
}

function genUuid() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback: not cryptographically strong, but adequate for soft dedupe.
  const rnd = () => Math.random().toString(36).slice(2, 10);
  return `${rnd()}-${rnd()}-${rnd()}-${Date.now().toString(36)}`;
}

export function getSessionId() {
  let id = safeRead(SESSION_KEY);
  if (!id) {
    id = genUuid();
    safeWrite(SESSION_KEY, id);
  }
  return id;
}

function stateKey(slug) {
  return `${STATE_KEY_PREFIX}${slug}`;
}

function readState(slug) {
  const raw = safeRead(stateKey(slug));
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writeState(slug, patch) {
  const next = { ...readState(slug), ...patch };
  safeWrite(stateKey(slug), JSON.stringify(next));
  return next;
}

export function getSurveyState(slug) {
  const s = readState(slug);
  return {
    submittedAt: s.submitted_at || null,
    skippedAt: s.skipped_at || null,
  };
}

export function shouldShowSurvey(slug) {
  const { submittedAt, skippedAt } = getSurveyState(slug);
  return !submittedAt && !skippedAt;
}

export function markSurveySubmitted(slug) {
  writeState(slug, { submitted_at: new Date().toISOString() });
}

export function markSurveySkipped(slug) {
  writeState(slug, { skipped_at: new Date().toISOString() });
}
